﻿import { Map, MapboxGeoJSONFeature, MapMouseEvent, Point, PointLike } from "mapbox-gl";

import MapBoxUtilities from "../utilities/MapBoxUtilities"

export interface MapBoxInteractionsOptions {
    /** Pixel tolerance for considering the pointer close enough to a segment or vertex for detection. Default is 4. */
    hitTolerance?: number,
    /** Max pixel distance allowed to be covered between a pointer down and up event when registering a click event. Default 8. */
    clickTolerance?: number,
    /** Default is pointer. */
    cursor?: string,
    /** Callback for hover event. */
    onHover?: (features?: MapboxGeoJSONFeature[], e?: MapMouseEvent) => void,
    /** Disable default hover and only use onHover callback function (useful when multiple layers with overlapping features are used)  */
    disableDefaultOnHoverInteraction?: boolean;
    /** Callback for click event. */
    onClick?: (features?: MapboxGeoJSONFeature[], e?: MapMouseEvent) => void,
    /** Disable default click and only use onClick callback function (useful when multiple layers with overlapping features are used)  */
    disableDefaultOnClickInteraction?: boolean;
    /** Layers in mapbox style sheet to be considered for detection. */
    layers?: string[]
}

/**
 * Handles pointer hover and click events on a mapbox map
 * */
class MapBoxInteractions {

    private readonly options: MapBoxInteractionsOptions;
    private clickedPoint: Point | undefined;

    constructor(
        private map: Map,
        options?: MapBoxInteractionsOptions) {

        this.options = {
            hitTolerance: 4,
            clickTolerance: 8,
            layers: [],
            ...options
        };

        const { onHover, onClick, disableDefaultOnHoverInteraction, disableDefaultOnClickInteraction } = this.options;

        if (onHover && !disableDefaultOnHoverInteraction) {
            map.on("mousemove", this.mousemove);
            map.on("mouseout", this.mouseleave);
            map.on("zoomstart", this.mouseleave);
        }

        if (onClick && !disableDefaultOnClickInteraction) {
            map.on("mousedown", this.mousedown);
            map.on("mouseup", this.mouseup);
        }
    }

    public dispose() {
        const { onHover, onClick } = this.options;
        if (onHover) {
            this.map.off("mousemove", this.mousemove);
            this.map.off("mouseout", this.mouseleave);
            this.map.off("zoomstart", this.mouseleave);
        }

        if (onClick) {
            this.map.off("mousedown", this.mousedown);
            this.map.off("mouseup", this.mouseup);
        }
    }

    public setLayers(layers: string[]) {
        this.options.layers = layers;
    }

    public getLayers() {
        return this.options.layers?.slice();
    }

    private mousemove = (e: MapMouseEvent) => {
        this.setHighlighted(this.getFeatures(e.point), e);
    }

    private mouseleave = (e: MapMouseEvent) => {
        const { onHover } = this.options;

        if (onHover) {
            // this does't work for element on top of canvas..
            //if (e) {
            //    if (e.originalEvent && !e.originalEvent.relatedTarget) {
            //        this.setHighlighted(undefined);
            //        return;
            //    }

            //    if (e?.point) {
            //        const { x, y } = e.point;
            //        const { offsetLeft: l, offsetTop: t, offsetWidth: w, offsetHeight: h } = this.map.getCanvas()
            //        const m = 12;
            //        // skip if still on canvas
            //        if (x > (l + m) && y > (t + m) && x < (l + w - m) && y < (t + h - m))
            //            return;
            //    }
            //}
            this.setHighlighted(undefined);
        }
    }

    private mousedown = (e: MapMouseEvent) => this.clickedPoint = e.point;

    private mouseup = (e: MapMouseEvent) => {
        if (this.isValidClick(e)) {
            if (this.clickedPoint) 
                this.setSelected(this.getFeatures(this.clickedPoint), e);            
        };
    }

    private isValidClick = (e: MapMouseEvent) => {
        const { clickTolerance } = this.options;
        return this.clickedPoint && e.point.dist(this.clickedPoint) <= (clickTolerance || 0);
    }

    private setHighlighted(features: MapboxGeoJSONFeature[] | undefined, e?: MapMouseEvent) {
        const { onHover } = this.options;
        if (onHover)             
            onHover(features, e);        
    }

    private setSelected(features: MapboxGeoJSONFeature[] | undefined, e?: MapMouseEvent) {
        const { onClick } = this.options;
        if (onClick) {
            if (!features?.length)
                features = undefined;

            onClick(features, e);
        }
    }

    public getFeatures(point: Point) {
        const { hitTolerance, layers } = this.options;
        const d = hitTolerance ?? 0;
        const extent: [PointLike, PointLike] = [
            [point.x - d, point.y - d],
            [point.x + d, point.y + d]
        ]

        const queryOptions = {
            validate: false,
            layers,
        }

        var featuresInExtent = this.map.queryRenderedFeatures(extent, queryOptions);
        if (featuresInExtent.length > 1) {
            MapBoxUtilities.sortFeaturesBySize(featuresInExtent);

            // If all types are the same, prioritize features directly at mouse position (if any)
            if (featuresInExtent.every(f => f.layer.type === featuresInExtent[0].layer.type)) {
                const featuresAtPoint = new Set(this.map.queryRenderedFeatures(point, queryOptions).map(f => f.id));
                if (featuresAtPoint.size)
                    featuresInExtent.sort((a, b) => Number(featuresAtPoint.has(b.id)) - Number(featuresAtPoint.has(a.id)));
            }
            else {
                MapBoxUtilities.sortFeaturesByType(featuresInExtent);
            }

            // Remove duplicate features
            const uniqueFeatures: { [key: number | string]: MapboxGeoJSONFeature } = {};
            featuresInExtent.forEach(feature => {
                if (feature.id !== undefined)
                    uniqueFeatures[feature.id] = feature;
            })
            featuresInExtent = Object.values(uniqueFeatures);
        }

        return featuresInExtent.length ? featuresInExtent : undefined;
    }
}

export default MapBoxInteractions;